import type { TargetStatus, WindowSummary } from '@core/line-probe';
import { Heading } from '@core/ui';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { JitterChart } from './charts/JitterChart';
import { LossChart } from './charts/LossChart';
import { PingChart } from './charts/PingChart';
import type { TimeAxis } from './charts/TimeChart';
import { UploadChart } from './charts/UploadChart';
import { formatMs } from './charts/values';
import { numberFormat } from './format';
import { backgroundSpans, chartSlots, headline, unmeasuredSpans, type SecondBucket, type Span } from './perSecond';
import type { LoadStep } from './useLineQuality';

function useStatusText(status: TargetStatus | undefined): string {
    const { t } = useTranslation();
    switch (status?.state) {
        case 'measuring':
            return t('TestSuite.Line.Status.Measuring');
        case 'tcp':
            return t('TestSuite.Line.Status.Tcp');
        case 'failed':
            return t('TestSuite.Line.Status.Failed', { reason: status.reason ?? '?' });
        case 'stopped':
            return t('TestSuite.Line.Status.Stopped');
        default:
            return t('TestSuite.Line.Status.Connecting');
    }
}

const Figure = ({ label, value, note }: { label: string; value: string; note: string }) => (
    <div>
        <dt className="text-sm">
            {label} <span className="opacity-70">({note})</span>
        </dt>
        <dd className="text-2xl font-semibold">{value}</dd>
    </div>
);

/**
 * One target: its status, the four header figures and the charts on the shared time axis. Memoised:
 * it renders on the one-second tick, not on every status or window of another target.
 */
export const TargetPanel = memo(function TargetPanel({
    target,
    status,
    buckets,
    stalled,
    windows,
    axis,
    through,
    originWall,
    loadSteps
}: {
    target: string;
    status: TargetStatus | undefined;
    buckets: SecondBucket[];
    /** The seconds in which the browser's main thread stalled. */
    stalled: Span[];
    windows: WindowSummary[];
    axis: Omit<TimeAxis, 'unmeasured' | 'stalled' | 'background'>;
    /** The newest settled second; below 0, no second has settled yet and there is nothing to chart. */
    through: number;
    originWall: number;
    /** Set on the target that carries the upload load. */
    loadSteps?: LoadStep[];
}) {
    const { t, i18n } = useTranslation();
    const statusText = useStatusText(status);
    const none = t('TestSuite.Line.NoValue');
    const fullAxis: TimeAxis = { ...axis, unmeasured: unmeasuredSpans(buckets, axis.from, axis.to), stalled, background: backgroundSpans(windows, originWall) };
    const slots = chartSlots(buckets, axis.from, axis.to, axis.slot);
    const figures = headline(buckets, through);
    const silence = windows.length ? Math.max(...windows.map((w) => w.silenceMaxMs)) : null;
    const warn = status?.state === 'tcp' || status?.state === 'failed';

    return (
        <section className="mt-6 first:mt-0" aria-labelledby={`line-target-${target}`}>
            <Heading variant="h2" classNames="mb-1">
                <span id={`line-target-${target}`}>{t('TestSuite.Line.Target', { target })}</span>
            </Heading>
            <p role="status" className={`mb-3 ${warn ? 'text-warning font-semibold' : ''}`}>
                {statusText}
            </p>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <Figure
                    label={t('TestSuite.Line.Figure.Ping')}
                    note={t('TestSuite.Line.Figure.Recent')}
                    value={formatMs(figures.rttP50, none, i18n.language)}
                />
                <Figure
                    label={t('TestSuite.Line.Figure.Jitter')}
                    note={t('TestSuite.Line.Figure.Recent')}
                    value={formatMs(figures.jitterP50, none, i18n.language)}
                />
                <Figure
                    label={t('TestSuite.Line.Figure.Loss')}
                    note={t('TestSuite.Line.Figure.WholeRun')}
                    value={figures.lossPercent === null ? none : `${numberFormat(i18n.language, 2).format(figures.lossPercent)} %`}
                />
                <Figure label={t('TestSuite.Line.Figure.Silence')} note={t('TestSuite.Line.Figure.WholeRun')} value={formatMs(silence, none, i18n.language)} />
            </dl>
            {through >= 0 && (
                <div className="grid gap-6 lg:grid-cols-2">
                    <PingChart slots={slots} axis={fullAxis} />
                    <JitterChart slots={slots} axis={fullAxis} />
                    <LossChart slots={slots} axis={fullAxis} />
                    {loadSteps?.some((s) => s.kbps > 0) && <UploadChart slots={slots} axis={fullAxis} steps={loadSteps} />}
                </div>
            )}
        </section>
    );
});
